<!--
	This is the users page for managing organization members and invitations,
	it uses the dashboard layout in: "./layouts/Dashboard.vue" .
 -->

<template>
	<div class="users">

		<!-- User Management Header -->
		<a-row :gutter="24" type="flex" class="mb-24">
			<a-col :span="24">
				<a-card :bordered="false" class="header-solid h-full">
					<template #title>
						<h5 class="font-semibold">Member Management</h5>
						<p>Manage organization members and invitations</p>
					</template>
					<template #extra v-if="isAdmin">
						<a-button type="primary" @click="showInviteModal = true">
							<a-icon type="mail" />
							Invite User
						</a-button>
					</template>
				</a-card>
			</a-col>
		</a-row>

		<!-- Pending Invitations -->
		<a-row :gutter="24" type="flex" class="mb-24" v-if="isAdmin && invitations.length > 0">
			<a-col :span="24">
				<a-card :bordered="false" title="Pending Invitations">
					<a-table
						:columns="invitationColumns"
						:data-source="invitations"
						:pagination="false"
						:row-key="record => record.id"
					>
						<template #status="status">
							<a-tag :color="getStatusColor(status)">{{ status }}</a-tag>
						</template>
						<template #expiresAt="expiresAt">
							{{ formatDate(expiresAt) }}
						</template>
						<template #actions="text, record">
							<a-button
								type="link"
								size="small"
								@click="cancelInvitation(record.id)"
								danger
							>
								Cancel
							</a-button>
						</template>
					</a-table>
				</a-card>
			</a-col>
		</a-row>

		<!-- Organization Members -->
		<a-row :gutter="24" type="flex">
			<a-col :span="24" class="mb-24">
				<a-card :bordered="false" title="Organization Members">
					<a-table
						:columns="memberColumns"
						:data-source="members"
						:pagination="{ pageSize: 10 }"
						:row-key="record => record.userId"
					>
						<template #user="text, record">
							<div class="author-info">
								<div class="author-details">
									<h6 class="author-name">{{ record.firstName }} {{ record.lastName }}</h6>
									<p class="author-email">{{ record.email }}</p>
								</div>
							</div>
						</template>
						<template #role="role">
							<a-tag :color="getRoleColor(role)">{{ role }}</a-tag>
						</template>
						<template #enabled="enabled">
							<a-badge :status="enabled ? 'success' : 'error'" :text="enabled ? 'Active' : 'Inactive'" />
						</template>
						<template #actions="text, record">
							<template v-if="isAdmin">
								<a-button
									type="link"
									size="small"
									@click="editMemberRole(record)"
									:disabled="!record.enabled"
								>
									Edit Role
								</a-button>
								<a-divider type="vertical" />
								<a-button
									type="link"
									size="small"
									@click="removeMember(record)"
									danger
									:disabled="!record.enabled"
								>
									Remove
								</a-button>
							</template>
							<span v-else>-</span>
						</template>
					</a-table>
				</a-card>
			</a-col>
		</a-row>

		<!-- Invite User Modal -->
		<a-modal
			title="Invite User to Organization"
			:visible="showInviteModal"
			@ok="sendInvitation"
			@cancel="showInviteModal = false"
			:confirmLoading="sendingInvitation"
		>
			<a-form :form="inviteForm" layout="vertical">
				<a-form-item label="Email Address">
					<a-input
						v-decorator="[
							'email',
							{
								rules: [
									{ required: true, message: 'Please input email address!' },
									{ type: 'email', message: 'Please enter a valid email!' }
								]
							}
						]"
						placeholder="user@example.com"
					/>
				</a-form-item>
				<a-form-item label="Role">
					<a-select
						v-decorator="[
							'role',
							{
								rules: [{ required: true, message: 'Please select a role!' }],
								initialValue: 'OPERATOR'
							}
						]"
						placeholder="Select role"
					>
						<a-select-option v-for="role in roles" :key="role.name" :value="role.name">
							<div>
								<strong>{{ role.displayName }}</strong>
								<div style="font-size: 12px; color: #888;">{{ role.description }}</div>
							</div>
						</a-select-option>
					</a-select>
				</a-form-item>
				<a-alert
					message="An invitation email will be sent to this address"
					description="The invitation will expire after 24 hours."
					type="info"
					show-icon
				/>
			</a-form>
		</a-modal>

		<!-- Edit Member Role Modal -->
		<a-modal
			title="Edit Member Role"
			:visible="showEditRoleModal"
			@ok="updateMemberRole"
			@cancel="showEditRoleModal = false"
			:confirmLoading="updatingRole"
		>
			<a-form :form="editRoleForm" layout="vertical">
				<a-form-item label="Member">
					<a-input
						:value="selectedMember.firstName + ' ' + selectedMember.lastName"
						disabled
					/>
				</a-form-item>
				<a-form-item label="Role">
					<a-select
						v-decorator="[
							'role',
							{
								rules: [{ required: true, message: 'Please select a role!' }],
								initialValue: selectedMember.role
							}
						]"
						placeholder="Select role"
					>
						<a-select-option v-for="role in roles" :key="role.name" :value="role.name">
							<div>
								<strong>{{ role.displayName }}</strong>
								<div style="font-size: 12px; color: #888;">{{ role.description }}</div>
							</div>
						</a-select-option>
					</a-select>
				</a-form-item>
			</a-form>
		</a-modal>

	</div>
</template>

<script>
	import { OrganizationManager, authFetch } from '../auth.js';

	export default ({
		data() {
			return {
				members: [],
				invitations: [],
				roles: [],
				showInviteModal: false,
				showEditRoleModal: false,
				sendingInvitation: false,
				updatingRole: false,
				selectedMember: {},
				currentUserId: null,
				memberColumns: [
					{
						title: 'NAME',
						dataIndex: 'user',
						scopedSlots: { customRender: 'user' },
					},
					{
						title: 'ROLE',
						dataIndex: 'role',
						scopedSlots: { customRender: 'role' },
					},
					{
						title: 'STATUS',
						dataIndex: 'enabled',
						scopedSlots: { customRender: 'enabled' },
					},
					{
						title: 'ACTIONS',
						scopedSlots: { customRender: 'actions' },
						width: 200,
					},
				],
				invitationColumns: [
					{
						title: 'EMAIL',
						dataIndex: 'email',
					},
					{
						title: 'ROLE',
						dataIndex: 'role',
					},
					{
						title: 'STATUS',
						dataIndex: 'status',
						scopedSlots: { customRender: 'status' },
					},
					{
						title: 'INVITED BY',
						dataIndex: 'invitedBy',
					},
					{
						title: 'EXPIRES',
						dataIndex: 'expiresAt',
						scopedSlots: { customRender: 'expiresAt' },
					},
					{
						title: 'ACTIONS',
						scopedSlots: { customRender: 'actions' },
						width: 120,
					},
				],
			}
		},
		beforeCreate() {
			this.inviteForm = this.$form.createForm(this, { name: 'inviteUser' });
			this.editRoleForm = this.$form.createForm(this, { name: 'editRole' });
		},
		mounted() {
			this.fetchCurrentUser();
			this.fetchMembers();
			this.fetchInvitations();
			this.fetchRoles();
		},
		computed: {
			isAdmin() {
				return OrganizationManager.isAdmin();
			}
		},
		methods: {
			async fetchCurrentUser() {
				try {
					const apiBaseUrl = process.env.VUE_APP_API_BASE_URL || 'http://localhost:8080';
					const response = await authFetch(`${apiBaseUrl}/api/auth/profile`);
					const userData = await response.json();
					this.currentUserId = userData.id;
				} catch (error) {
					console.error('Error fetching current user:', error);
				}
			},

			async fetchMembers() {
				try {
					const url = OrganizationManager.buildOrgUrl('/members');
					const response = await authFetch(url);

					if (response.ok) {
						this.members = await response.json();
					} else {
						this.$message.error('Failed to fetch members');
					}
				} catch (error) {
					console.error('Error fetching members:', error);
					this.$message.error('Error fetching members');
				}
			},

			async fetchInvitations() {
				try {
					const url = OrganizationManager.buildOrgUrl('/invitations');
					const response = await authFetch(url);

					if (response.ok) {
						const allInvitations = await response.json();
						// Only show pending invitations
						this.invitations = allInvitations.filter(inv => inv.status === 'PENDING');
					} else {
						this.$message.error('Failed to fetch invitations');
					}
				} catch (error) {
					console.error('Error fetching invitations:', error);
					this.$message.error('Error fetching invitations');
				}
			},

			async fetchRoles() {
				try {
					const apiBaseUrl = process.env.VUE_APP_API_BASE_URL || 'http://localhost:8080';
					const response = await authFetch(`${apiBaseUrl}/api/organizations/roles`);

					if (response.ok) {
						this.roles = await response.json();
					} else {
						console.error('Failed to fetch roles');
					}
				} catch (error) {
					console.error('Error fetching roles:', error);
				}
			},

			async sendInvitation() {
				this.inviteForm.validateFields(async (err, values) => {
					if (!err) {
						this.sendingInvitation = true;
						try {
							const url = OrganizationManager.buildOrgUrl('/invitations');
							const response = await authFetch(url, {
								method: 'POST',
								body: JSON.stringify(values)
							});

							const data = await response.json();

							if (response.ok) {
								this.$message.success('Invitation sent successfully');
								this.showInviteModal = false;
								this.inviteForm.resetFields();
								this.fetchInvitations();
							} else {
								this.$message.error(data.error || 'Failed to send invitation');
							}
						} catch (error) {
							console.error('Error sending invitation:', error);
							this.$message.error('Error sending invitation');
						} finally {
							this.sendingInvitation = false;
						}
					}
				});
			},

			async cancelInvitation(invitationId) {
				try {
					const url = OrganizationManager.buildOrgUrl(`/invitations/${invitationId}`);
					const response = await authFetch(url, {
						method: 'DELETE'
					});

					if (response.ok) {
						this.$message.success('Invitation cancelled successfully');
						this.fetchInvitations();
					} else {
						this.$message.error('Failed to cancel invitation');
					}
				} catch (error) {
					console.error('Error cancelling invitation:', error);
					this.$message.error('Error cancelling invitation');
				}
			},

			editMemberRole(member) {
				this.selectedMember = member;
				this.showEditRoleModal = true;
			},

			async updateMemberRole() {
				this.editRoleForm.validateFields(async (err, values) => {
					if (!err) {
						this.updatingRole = true;
						try {
							const url = OrganizationManager.buildOrgUrl(`/members/${this.selectedMember.userId}/role`);
							const response = await authFetch(url, {
								method: 'PUT',
								body: JSON.stringify({ role: values.role })
							});

							if (response.ok) {
								this.$message.success('Member role updated successfully');
								this.showEditRoleModal = false;
								this.editRoleForm.resetFields();
								this.fetchMembers();
							} else {
								this.$message.error('Failed to update member role');
							}
						} catch (error) {
							console.error('Error updating member role:', error);
							this.$message.error('Error updating member role');
						} finally {
							this.updatingRole = false;
						}
					}
				});
			},

			async removeMember(member) {
				this.$confirm({
					title: 'Remove Member',
					content: `Are you sure you want to remove ${member.firstName} ${member.lastName} from this organization?`,
					okText: 'Remove',
					okType: 'danger',
					cancelText: 'Cancel',
					onOk: async () => {
						try {
							const url = OrganizationManager.buildOrgUrl(`/members/${member.userId}`);
							const response = await authFetch(url, {
								method: 'DELETE'
							});

							if (response.ok) {
								this.$message.success('Member removed successfully');
								this.fetchMembers();
							} else {
								this.$message.error('Failed to remove member');
							}
						} catch (error) {
							console.error('Error removing member:', error);
							this.$message.error('Error removing member');
						}
					}
				});
			},

			getRoleColor(role) {
				const colors = {
					ADMIN: 'red',
					OPERATOR: 'blue',
					VIEWER: 'green'
				};
				return colors[role] || 'default';
			},

			getStatusColor(status) {
				const colors = {
					PENDING: 'orange',
					ACCEPTED: 'green',
					EXPIRED: 'red',
					CANCELLED: 'gray'
				};
				return colors[status] || 'default';
			},

			formatDate(dateString) {
				if (!dateString) return '';
				const date = new Date(dateString);
				return date.toLocaleString();
			}
		}
	})
</script>

<style lang="scss" scoped>
	.users {
		.author-info {
			display: flex;
			align-items: center;

			.author-details {
				.author-name {
					margin: 0;
					font-size: 14px;
					font-weight: 600;
				}

				.author-email {
					margin: 0;
					font-size: 12px;
					color: #8c8c8c;
				}
			}
		}
	}
</style>
